// CarrierAdjustments — merged Reconciliation + ByeLabel Sync
// Three tabs: Pending | Pushed | Settings (auto-push + exclusions)
// Fixed-height layout — no full-page scroll.

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth, authHeaders } from '../lib/auth'
import { Icon } from '../components/Icon'
import { CopyCell } from '../components/CopyCell'
import { RebillEvidence } from '../components/RebillEvidence'
import { RecordHistory } from '../components/RecordHistory'
import type { SortState } from '../hooks/useSortable'
import { SortableHeader } from '../components/SortableHeader'

const API = '/api/byelabel-sync'


function fmt(n: number | string | null | undefined) {
  if (n == null || n === '') return '—'
  const num = Number(n)
  if (isNaN(num)) return '—'
  const abs = Math.abs(num)
  const s = abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return num < 0 ? `-$${s}` : `$${s}`
}

function ageDays(dateStr: string | null | undefined): number {
  if (!dateStr) return 0
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86_400_000)
}

function AgeBadge({ dateStr }: { dateStr: string | null | undefined }) {
  const d = ageDays(dateStr)
  const cls = d > 30 ? 'bg-flame-100 text-flame-700' : d > 7 ? 'bg-honey-100 text-honey-700' : 'bg-ink-100 text-ink-500'
  return <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${cls}`}>{d}d</span>
}

// Shift-click range selection: the ids between the last clicked row and this
// one, in the order the table shows them (only rows that can be selected).
function rangeIds(order: number[], from: number | null, to: number): number[] {
  const a = from == null ? -1 : order.indexOf(from), b = order.indexOf(to)
  if (a < 0 || b < 0) return [to]
  return order.slice(Math.min(a, b), Math.max(a, b) + 1)
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'CENSUS_PENDING') return <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-honey-100 text-honey-700">Waiting</span>
  if (status === 'PENDING')        return <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-mint-100 text-mint-700">Pending</span>
  return <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-ink-100 text-ink-500">{status}</span>
}


function scopeBadge(scope: string) {
  const map: Record<string, string> = { past: 'Past only', future: 'Future only', both: 'Both' }
  return <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-ink-100 text-ink-600">{map[scope] ?? scope}</span>
}

type Scope = 'past' | 'future' | 'both'
const SCOPES: { value: Scope; label: string; desc: string }[] = [
  { value: 'past',   label: 'Past only',   desc: 'Exclude from manual push UI' },
  { value: 'future', label: 'Future only',  desc: 'Skip auto-push on new invoices' },
  { value: 'both',   label: 'Both',         desc: 'Exclude from all push paths' },
]

const CARRIERS   = ['FEDEX','DHL','USPS','ASENDIA','UPS','APC','EPOST','CANADAPOST','EVRI','INTELCOM','UNIUNI']
const CATEGORIES = [
  'UNUSED_LABEL_REFUND','POSTAGEDELTA','USPS_LABEL_REFUND','OVERCHARGE',
  'CUSTOMS_REFUND','UNMANIFESTED','WEIGHT_REWEIGHT','DIM_REWEIGHT',
  'TRANSPORT_NET','ZONE_CHANGE','NONSTANDARD_FEE','VENDOR_FEE',
]

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface PendingRebill {
  rebill_id: number
  customer_name: string
  shipment_number: string
  carrier: string
  adjustment_category: string | null
  adjustment_name: string | null
  rebill_amount: number
  status: string
  carrier_invoice_date: string | null
  shipment_date: string | null
  byelabel_push_error: string | null
}

interface PushJob {
  job_id: string | null
  total: number
  pushed: number
  failed: number
  skipped: number
  running: boolean
  error: string | null
}

interface PushedRebill {
  rebill_id: number
  customer_name: string
  shipment_number: string
  carrier: string
  adjustment_category: string | null
  adjustment_name: string | null
  rebill_amount: number
  invoice_number: string | null
  shipment_date: string | null
  byelabel_pushed_at: string
}

interface RebillDetail {
  rebill_id: number
  customer_name: string
  shipment_number: string
  carrier: string
  carrier_invoice_date: string | null
  shipment_date: string | null
  adjustment_category: string | null
  adjustment_name: string | null
  cost_amount: number
  markup_pct: number
  rebill_amount: number
  rebill_reason: string
  status: string
  invoice_number: string | null
  invoiced_at: string | null
  byelabel_pushed_at: string | null
  byelabel_push_error: string | null
  evidence_json: unknown
}

interface Exclusion {
  customer_name: string
  scope: string
  created_at: string | null
}

// ---------------------------------------------------------------------------
// Filter bar (shared between Pending and Pushed tabs)
// ---------------------------------------------------------------------------
interface FilterState {
  customer: string
  carrier: string
  category: string
  shipment: string
  since: string
  until: string
  status?: string   // Pending tab only: '' (all) | 'PENDING' | 'CENSUS_PENDING'
  ageMin?: string   // Pending tab only: days since the carrier invoice date (Age column)
  ageMax?: string
}

function FilterBar({
  f, customers, onChange, onApply, onClear, showStatus = false,
}: {
  f: FilterState
  customers: { customer_name: string }[]
  onChange: (next: Partial<FilterState>) => void
  onApply: () => void
  onClear: () => void
  showStatus?: boolean
}) {
  return (
    <div className="flex flex-wrap gap-2 items-end">
      <div className="flex flex-col gap-0.5">
        <label className="text-[10px] font-semibold text-ink-400 uppercase tracking-wide">Customer</label>
        <select value={f.customer} onChange={e => onChange({ customer: e.target.value })}
          className="h-8 pl-2 pr-6 border border-ink-200 rounded-md text-[12px] bg-white focus-ring">
          <option value="">All</option>
          {customers.map(c => <option key={c.customer_name} value={c.customer_name}>{c.customer_name}</option>)}
        </select>
      </div>
      <div className="flex flex-col gap-0.5">
        <label className="text-[10px] font-semibold text-ink-400 uppercase tracking-wide">Carrier</label>
        <select value={f.carrier} onChange={e => onChange({ carrier: e.target.value })}
          className="h-8 pl-2 pr-6 border border-ink-200 rounded-md text-[12px] bg-white focus-ring">
          <option value="">All</option>
          {CARRIERS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div className="flex flex-col gap-0.5">
        <label className="text-[10px] font-semibold text-ink-400 uppercase tracking-wide">Category</label>
        <select value={f.category} onChange={e => onChange({ category: e.target.value })}
          className="h-8 pl-2 pr-6 border border-ink-200 rounded-md text-[12px] bg-white focus-ring">
          <option value="">All</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      {showStatus && (
        <div className="flex flex-col gap-0.5">
          <label className="text-[10px] font-semibold text-ink-400 uppercase tracking-wide">Status</label>
          <select value={f.status ?? ''} onChange={e => onChange({ status: e.target.value })}
            className="h-8 pl-2 pr-6 border border-ink-200 rounded-md text-[12px] bg-white focus-ring">
            <option value="">All</option>
            <option value="PENDING">Pending (ready to push)</option>
            <option value="CENSUS_PENDING">Waiting (held for census)</option>
          </select>
        </div>
      )}
      <div className="flex flex-col gap-0.5">
        <label className="text-[10px] font-semibold text-ink-400 uppercase tracking-wide">Tracking</label>
        <input type="text" value={f.shipment} onChange={e => onChange({ shipment: e.target.value })}
          onKeyDown={e => e.key === 'Enter' && onApply()}
          placeholder="partial match"
          className="h-8 px-2 border border-ink-200 rounded-md text-[12px] focus-ring w-36" />
      </div>
      <div className="flex flex-col gap-0.5">
        <label className="text-[10px] font-semibold text-ink-400 uppercase tracking-wide">Ship From</label>
        <input type="date" value={f.since}
          onChange={e => { onChange({ since: e.target.value }); if (f.until && e.target.value > f.until) onChange({ until: '' }) }}
          className="h-8 px-2 border border-ink-200 rounded-md text-[12px] focus-ring" />
      </div>
      <div className="flex flex-col gap-0.5">
        <label className="text-[10px] font-semibold text-ink-400 uppercase tracking-wide">Ship To</label>
        <input type="date" value={f.until}
          onChange={e => { onChange({ until: e.target.value }); if (f.since && e.target.value < f.since) onChange({ since: '' }) }}
          className="h-8 px-2 border border-ink-200 rounded-md text-[12px] focus-ring" />
      </div>
      {showStatus && (
        <div className="flex flex-col gap-0.5">
          <label className="text-[10px] font-semibold text-ink-400 uppercase tracking-wide"
            title="Age = days since the carrier invoice date (the Age column)">Age (days)</label>
          <div className="flex items-center gap-1">
            <input type="number" min={0} value={f.ageMin ?? ''} placeholder="min"
              onChange={e => onChange({ ageMin: e.target.value })}
              onKeyDown={e => e.key === 'Enter' && onApply()}
              aria-label="Minimum age in days"
              className="h-8 px-2 border border-ink-200 rounded-md text-[12px] focus-ring w-16" />
            <span className="text-[11px] text-ink-400">to</span>
            <input type="number" min={0} value={f.ageMax ?? ''} placeholder="max"
              onChange={e => onChange({ ageMax: e.target.value })}
              onKeyDown={e => e.key === 'Enter' && onApply()}
              aria-label="Maximum age in days"
              className="h-8 px-2 border border-ink-200 rounded-md text-[12px] focus-ring w-16" />
          </div>
        </div>
      )}
      <button onClick={onClear}
        className="h-8 px-3 border border-ink-200 text-ink-500 rounded-md text-[12px] hover:bg-ink-50 transition-colors">
        Clear
      </button>
      <button onClick={onApply}
        className="h-8 px-4 bg-ink-800 text-white rounded-md text-[12px] font-medium hover:bg-ink-700 transition-colors">
        Apply
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Detail drawer
// ---------------------------------------------------------------------------
function DetailDrawer({ rebillId, onClose }: { rebillId: number | null; onClose: () => void }) {
  const { token } = useAuth()
  const { data: d, isLoading } = useQuery<RebillDetail>({
    queryKey: ['rebill-detail', rebillId],
    queryFn: () => fetch(`${API}/rebill/${rebillId}`, { headers: authHeaders(token) }).then(r => r.json()),
    enabled: rebillId != null,
  })
  if (rebillId == null) return null
  function Row({ label, value }: { label: string; value: ReactNode }) {
    return (
      <div className="flex items-start gap-2 py-1.5 border-b border-ink-50 last:border-0">
        <span className="text-[11px] font-medium text-ink-400 w-32 shrink-0 pt-0.5">{label}</span>
        <span className="text-[12px] text-ink-800 break-all">{value ?? '—'}</span>
      </div>
    )
  }
  return (
    <div className="fixed top-0 right-0 h-full w-[480px] z-50 bg-white border-l border-ink-200 shadow-2xl flex flex-col">
      <div className="flex items-center justify-between px-5 py-4 border-b border-ink-100 shrink-0">
        <span className="text-[14px] font-semibold text-ink-900">Rebill #{rebillId}</span>
        <button onClick={onClose} className="text-ink-400 hover:text-ink-700"><Icon name="x" className="w-4 h-4" /></button>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-4">
        {isLoading ? <p className="text-[13px] text-ink-400">Loading…</p> : !d ? <p className="text-[13px] text-ink-400">Not found</p> : (
          <div className="space-y-4">
            <div>
              <div className="text-[10px] font-semibold text-ink-400 uppercase tracking-wide mb-2">Identity</div>
              <Row label="Customer"   value={d.customer_name} />
              <Row label="Carrier"    value={d.carrier?.toUpperCase()} />
              <Row label="Status"     value={<StatusBadge status={d.status} />} />
              <Row label="Category"   value={d.adjustment_category} />
              <Row label="Name"       value={d.adjustment_name} />
            </div>
            <div>
              <div className="text-[10px] font-semibold text-ink-400 uppercase tracking-wide mb-2">Shipment</div>
              <Row label="Tracking"   value={<CopyCell value={d.shipment_number} />} />
              <Row label="Ship Date"  value={d.shipment_date?.slice(0, 10)} />
              <Row label="Inv Date"   value={d.carrier_invoice_date?.slice(0, 10)} />
            </div>
            <div>
              <div className="text-[10px] font-semibold text-ink-400 uppercase tracking-wide mb-2">Charge</div>
              <Row label="Carrier Cost" value={fmt(d.cost_amount)} />
              <Row label="Markup"     value={`${Number(d.markup_pct ?? 0).toFixed(1)}%`} />
              <Row label="Rebill"     value={<span className="font-semibold text-mint-700">{fmt(d.rebill_amount)}</span>} />
            </div>
            <RebillEvidence evidence={d.evidence_json} carrier={d.carrier} reason={d.rebill_reason} />
            <div>
              <div className="text-[10px] font-semibold text-ink-400 uppercase tracking-wide mb-2">Lifecycle</div>
              <Row label="Invoice #"  value={d.invoice_number} />
              <Row label="Invoiced"   value={d.invoiced_at?.slice(0, 10)} />
              <Row label="Pushed"     value={d.byelabel_pushed_at?.slice(0, 19).replace('T', ' ')} />
              {d.byelabel_push_error && (
                <Row label="Push Error" value={<span className="text-flame-600">{d.byelabel_push_error}</span>} />
              )}
            </div>
            <RecordHistory entity="customer_rebills" id={rebillId} />
          </div>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// KPI chips
// ---------------------------------------------------------------------------
interface Summary {
  pending_count: number
  census_count: number
  pending_amount: number
  pushed_month_count: number
}

function KpiChips() {
  const { token } = useAuth()

  const { data } = useQuery<Summary>({
    queryKey: ['adj-summary'],
    queryFn: () => fetch(`${API}/summary`, { headers: authHeaders(token) }).then(r => r.json()),
    staleTime: 60_000,
  })

  const readyCount  = data?.pending_count      ?? 0
  const waitingCount = data?.census_count      ?? 0
  const pendingAmt  = data?.pending_amount     ?? 0
  const pushedMonth = data?.pushed_month_count ?? 0

  function Chip({ label, value, color }: { label: string; value: string; color: string }) {
    return (
      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${color}`}>
        <span className="text-[13px] font-semibold">{value}</span>
        <span className="text-[11px] text-ink-500">{label}</span>
      </div>
    )
  }

  return (
    <div className="flex flex-wrap gap-2 shrink-0">
      <Chip label="ready to push" value={`${readyCount} rebills · ${fmt(pendingAmt)}`} color="border-mint-200 bg-mint-50" />
      <Chip label="waiting (held)" value={String(waitingCount)} color="border-honey-200 bg-honey-50" />
      <Chip label="pushed this month" value={String(pushedMonth)} color="border-ink-200 bg-ink-50" />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Pending tab
// ---------------------------------------------------------------------------
const PAGE = 100

function PendingTab() {
  const qc = useQueryClient()
  const { token, can } = useAuth()
  // push / void / reset need manage_rebills (enforced by the API too)
  const canManage = can('manage_rebills')
  const { data: customers = [] } = useQuery<{ customer_name: string }[]>({
    queryKey: ['adj-customers'],
    queryFn: () => fetch(`${API}/customers`, { headers: authHeaders(token) }).then(r => r.json()).then(d => Array.isArray(d) ? d : []),
  })

  const [draft, setDraft] = useState<FilterState>({ customer: '', carrier: '', category: '', shipment: '', since: '', until: '', status: '' })
  const [applied, setApplied] = useState<FilterState>({ customer: '', carrier: '', category: '', shipment: '', since: '', until: '', status: '' })
  const [page, setPage] = useState(0)

  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [selectAllMatching, setSelectAllMatching] = useState(false)
  const [detailId, setDetailId] = useState<number | null>(null)
  const [confirmVoid, setConfirmVoid] = useState(false)
  const [confirmPushAll, setConfirmPushAll] = useState(false)
  // a push / void confirmation is for the selection it was shown for only:
  // switching to "all filtered" (or another filter) must ask again
  useEffect(() => { setConfirmPushAll(false); setConfirmVoid(false) }, [selectAllMatching, applied])
  const [confirmDispute, setConfirmDispute] = useState(false)
  const [disputeReason, setDisputeReason] = useState('')
  // a changed selection disarms the "Confirm Dispute" step
  useEffect(() => { setConfirmDispute(false) }, [selected])
  const [banner, setBanner] = useState<{ type: 'ok' | 'err'; msg: string } | null>(null)
  const [pendingSort, setPendingSort] = useState<SortState>({ col: 'shipment_date', dir: 'desc' })

  function onPendingSort(col: string) {
    setPendingSort(prev => prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' })
    setPage(0)
  }

  const params = new URLSearchParams()
  if (applied.customer)  params.set('customer',  applied.customer)
  if (applied.carrier)   params.set('carrier',   applied.carrier)
  if (applied.category)  params.set('category',  applied.category)
  if (applied.shipment)  params.set('shipment',  applied.shipment)
  if (applied.since)     params.set('since',     applied.since)
  if (applied.until)     params.set('until',     applied.until)
  if (applied.status)    params.set('status',    applied.status)
  if (applied.ageMin)    params.set('age_min',   applied.ageMin)
  if (applied.ageMax)    params.set('age_max',   applied.ageMax)
  params.set('limit', String(PAGE))
  params.set('offset', String(page * PAGE))
  params.set('sort_col', pendingSort.col)
  params.set('sort_dir', pendingSort.dir)

  const { data, isLoading, isFetching } = useQuery<{ rows: PendingRebill[]; total: number; total_pending?: number; total_waiting?: number; total_amount?: number; total_pending_amount?: number }>({
    queryKey: ['adj-pending', applied, page, pendingSort],
    queryFn: () => fetch(`${API}/pending?${params}`, { headers: authHeaders(token) }).then(r => r.json())
      .then(d => Array.isArray(d?.rows) ? d : { rows: [], total: 0 }),
  })

  const rows     = data?.rows  ?? []
  const total    = data?.total ?? 0
  // Split of the filtered total: "push all" pushes only Pending, "void all" voids both.
  const totalPending = data?.total_pending ?? total
  // "select all filtered" with no filter = every pending rebill: push / void
  // then need a second click that shows the count and the amount
  const unfiltered = !(applied.customer || applied.carrier || applied.category || applied.shipment
                       || applied.since || applied.until || applied.ageMin || applied.ageMax)
  const allAtOnce = selectAllMatching && unfiltered
  const usd = (n: number | undefined) => '$' + (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const totalWaiting = data?.total_waiting ?? 0
  const pages    = Math.ceil(total / PAGE)
  const pushable = rows.filter(r => r.status === 'PENDING')

  const EMPTY_FILTER: FilterState = { customer: '', carrier: '', category: '', shipment: '', since: '', until: '', status: '' }

  function applyFilters() {
    setApplied({ ...draft })
    setPage(0)
    setSelected(new Set())
    setSelectAllMatching(false)
    setConfirmVoid(false)
  }

  function clearFilters() {
    setDraft(EMPTY_FILTER)
    setApplied(EMPTY_FILTER)
    setPage(0)
    setSelected(new Set())
    setSelectAllMatching(false)
    setConfirmVoid(false)
  }

  function toggleAll() {
    if (selected.size === pushable.length && pushable.length > 0) {
      setSelected(new Set())
      setSelectAllMatching(false)
    } else {
      setSelected(new Set(pushable.map(r => r.rebill_id)))
      setSelectAllMatching(false)
    }
  }
  // shift-click selects / clears every pushable row between the last click and this one
  const lastClicked = useRef<number | null>(null)
  function toggleRow(id: number, shift = false) {
    // a hand-picked change ends "select all filtered" - otherwise Push / Void
    // would still act on every filtered row, including one just unticked
    setSelectAllMatching(false)
    const ids = shift ? rangeIds(pushable.map(r => r.rebill_id), lastClicked.current, id) : [id]
    setSelected(prev => {
      const s = new Set(prev); const on = !s.has(id)
      ids.forEach(x => on ? s.add(x) : s.delete(x))
      return s
    })
    lastClicked.current = id
  }

  // ── Background push (Sahin 2026-10-19) ─────────────────────────────────
  // Push starts a job on the server and returns at once; the page polls its
  // progress ("Pushed 20 / 100") until it is done. A push already running
  // (e.g. started before a page reload) is picked up again on load.
  const [pushJobId, setPushJobId] = useState<string | null>(null)

  useEffect(() => {
    fetch(`${API}/push-jobs/current`, { headers: authHeaders(token) }).then(r => r.json())
      .then((d: PushJob) => { if (d?.running && d.job_id) setPushJobId(d.job_id) })
      .catch(() => { /* no running push to resume */ })
  }, [token])

  const { data: pushJob, error: pushJobError } = useQuery<PushJob>({
    queryKey: ['push-job', pushJobId],
    enabled: !!pushJobId,
    queryFn: () => fetch(`${API}/push-jobs/${pushJobId}`, { headers: authHeaders(token) }).then(async r => {
      const d = await r.json()
      if (!r.ok) throw new Error(d?.error ?? `HTTP ${r.status}`)
      return d
    }),
    refetchInterval: q => (q.state.data && !q.state.data.running) ? false : 1500,
    retry: false,
  })

  const pushRunning = !!pushJobId && !pushJobError && (pushJob?.running ?? true)

  // Progress lost (the server restarted, or the session expired): stop waiting.
  // The push itself is saved rebill by rebill, so the Pushed tab is still right.
  useEffect(() => {
    if (!pushJobId || !pushJobError) return
    setBanner({ type: 'err', msg: `Push progress is no longer available (${pushJobError.message}). Check the Pushed tab — every rebill that went out is saved there.` })
    setPushJobId(null)
    qc.invalidateQueries({ queryKey: ['adj-pending'] })
    qc.invalidateQueries({ queryKey: ['adj-pushed'] })
  }, [pushJobError, pushJobId, qc])

  useEffect(() => {
    if (!pushJobId || !pushJob || pushJob.running) return
    const parts = [`Pushed ${pushJob.pushed} of ${pushJob.total}.`]
    if (pushJob.failed) parts.push(`${pushJob.failed} failed (see the Push Error on those rebills).`)
    if (pushJob.skipped) parts.push(`${pushJob.skipped} skipped (no longer pending, already pushed or on an open invoice draft).`)
    if (pushJob.error) parts.push(`Stopped: ${pushJob.error}`)
    setBanner({ type: pushJob.failed || pushJob.error ? 'err' : 'ok', msg: parts.join(' ') })
    setPushJobId(null)
    qc.invalidateQueries({ queryKey: ['adj-pending'] })
    qc.invalidateQueries({ queryKey: ['adj-pushed'] })
    qc.invalidateQueries({ queryKey: ['adj-summary'] })
    qc.invalidateQueries({ queryKey: ['nav-counts'] })
  }, [pushJob, pushJobId, qc])

  const startPush = (url: string, body: unknown) =>
    fetch(url, { method: 'POST', headers: { ...authHeaders(token), 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      .then(async r => {
        const d = await r.json().catch(() => ({ error: `HTTP ${r.status}` }))
        if (r.status === 409 && d?.job_id) return { ...d, resumed: true }   // one already running: follow it
        if (!r.ok || !d?.ok) throw new Error(d?.error ?? `HTTP ${r.status}`)
        return d
      })

  const onPushStarted = (d: PushJob & { resumed?: boolean; output?: string }) => {
    if (!d.job_id) { setBanner({ type: 'ok', msg: d.output ?? 'Nothing to push.' }); return }
    if (d.resumed) setBanner({ type: 'err', msg: 'A push is already running — showing its progress. Start yours when it is done.' })
    setSelected(new Set()); setSelectAllMatching(false)
    setPushJobId(d.job_id)
  }

  const pushMut = useMutation({
    mutationFn: (ids: number[]) => startPush(`${API}/push`, { rebill_ids: ids }),
    onSuccess: onPushStarted,
    onError: (e: Error) => setBanner({ type: 'err', msg: `Push not started: ${e.message}` }),
  })

  // Exactly the filter the list uses — the "…all matching" actions act on
  // the same rows the table shows (tracking + status included).
  const filterBody = () => ({
    customer: applied.customer || undefined,
    carrier:  applied.carrier  || undefined,
    category: applied.category || undefined,
    shipment: applied.shipment || undefined,
    since:    applied.since    || undefined,
    until:    applied.until    || undefined,
    status:   applied.status   || undefined,
    age_min:  applied.ageMin   || undefined,
    age_max:  applied.ageMax   || undefined,
    confirm_all: unfiltered ? true : undefined,   // only reached after the second click (allAtOnce)
  })

  const pushByFilterMut = useMutation({
    mutationFn: () => startPush(`${API}/push-by-filter`, filterBody()),
    onSuccess: onPushStarted,
    onError: (e: Error) => setBanner({ type: 'err', msg: `Push not started: ${e.message}` }),
  })

  // Move selected rebills to Disputes by hand: the carrier's charge looks wrong,
  // so it is disputed instead of billed (DRAFT dispute, rebill -> Disputed).
  const disputeMut = useMutation({
    mutationFn: (ids: number[]) =>
      fetch(`${API}/dispute`, { method: 'POST', headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
        body: JSON.stringify({ rebill_ids: ids, reason: disputeReason.trim() || undefined }) })
        .then(async r => {
          const d = await r.json().catch(() => ({ error: `HTTP ${r.status}` }))
          if (!r.ok || !d?.ok) throw new Error(d?.error ?? `HTTP ${r.status}`)
          return d
        }),
    onSuccess: d => {
      const extra = d.not_disputed ? ` ${d.not_disputed} not moved (not pending, already pushed, on an open invoice draft, or already decided).` : ''
      setBanner({ type: 'ok', msg: `Moved ${d.disputed} rebill(s) to Disputes as drafts.${extra}` })
      setSelected(new Set()); setConfirmDispute(false); setDisputeReason('')
      qc.invalidateQueries({ queryKey: ['adj-pending'] })
      qc.invalidateQueries({ queryKey: ['adj-summary'] })
      qc.invalidateQueries({ queryKey: ['nav-counts'] })
    },
    onError: (e: Error) => { setBanner({ type: 'err', msg: `Dispute failed: ${e.message}` }); setConfirmDispute(false) },
  })

  const voidMut = useMutation({
    mutationFn: (ids: number[]) =>
      fetch(`${API}/void`, { method: 'POST', headers: { ...authHeaders(token), 'Content-Type': 'application/json' }, body: JSON.stringify({ rebill_ids: ids }) }).then(r => r.json()),
    onSuccess: r => {
      setBanner({ type: 'ok', msg: `Voided ${r.voided ?? 0} rebill(s).` })
      setSelected(new Set()); setSelectAllMatching(false); setConfirmVoid(false)
      qc.invalidateQueries({ queryKey: ['adj-pending'] })
      qc.invalidateQueries({ queryKey: ['adj-summary'] })
      qc.invalidateQueries({ queryKey: ['nav-counts'] })
    },
  })

  const voidByFilterMut = useMutation({
    mutationFn: () =>
      fetch(`${API}/void-by-filter`, { method: 'POST', headers: { ...authHeaders(token), 'Content-Type': 'application/json' }, body: JSON.stringify(filterBody()) }).then(r => r.json()),
    onSuccess: r => {
      if (!r?.ok) { setBanner({ type: 'err', msg: r?.error ?? 'Void failed' }); setConfirmVoid(false); return }
      setBanner({ type: 'ok', msg: `Voided ${r.voided ?? 0} rebill(s).` })
      setSelected(new Set()); setSelectAllMatching(false); setConfirmVoid(false)
      qc.invalidateQueries({ queryKey: ['adj-pending'] })
      qc.invalidateQueries({ queryKey: ['adj-summary'] })
      qc.invalidateQueries({ queryKey: ['nav-counts'] })
    },
    onError: (e: Error) => { setBanner({ type: 'err', msg: `Void failed: ${e.message}` }); setConfirmVoid(false) },
  })

  return (
    <div className="flex flex-col h-full gap-3">
      <FilterBar f={draft} customers={customers} onChange={p => setDraft(prev => ({ ...prev, ...p }))} onApply={applyFilters} onClear={clearFilters} showStatus />

      {/* Action bar */}
      <div className="flex items-center gap-2 shrink-0 flex-wrap">
        <span className="text-[12px] text-ink-500">
          {total} rebill{total !== 1 ? 's' : ''}{isFetching ? ' · refreshing…' : ''}
        </span>

        {/* "Select all filtered" prompt — appears when current page is fully selected and there are more rows */}
        {!selectAllMatching && selected.size === pushable.length && pushable.length > 0 && total > rows.length && (
          <button
            onClick={() => setSelectAllMatching(true)}
            className="text-[12px] text-mint-700 underline underline-offset-2 hover:text-mint-900 ml-1">
            Select all {total} filtered →
          </button>
        )}

        {selectAllMatching && (
          <span className="text-[12px] font-semibold text-mint-700 ml-1">
            All {total} filtered selected{totalWaiting > 0 ? ` (${totalPending} pending · ${totalWaiting} waiting — push skips waiting)` : ''}
          </span>
        )}

        {(selected.size > 0 || selectAllMatching) && (
          <span className="text-[12px] text-ink-400 ml-1">
            {selectAllMatching ? total : selected.size} selected
          </span>
        )}

        <div className="ml-auto flex items-center gap-2">
          {(selected.size > 0 || selectAllMatching) && (
            <>
              {/* Push */}
              <button
                onClick={() => {
                  setBanner(null)
                  if (allAtOnce && !confirmPushAll) { setConfirmPushAll(true); setConfirmVoid(false); return }
                  setConfirmPushAll(false)
                  if (selectAllMatching) pushByFilterMut.mutate()
                  else pushMut.mutate([...selected])
                }}
                disabled={!canManage || pushMut.isPending || pushByFilterMut.isPending || pushRunning}
                title={canManage ? undefined : 'Needs the manage rebills permission'}
                className="h-8 px-4 bg-mint-600 text-white rounded-md text-[12px] font-semibold hover:bg-mint-700 disabled:opacity-50 transition-colors">
                {(pushMut.isPending || pushByFilterMut.isPending)
                  ? 'Starting…'
                  : pushRunning
                    ? 'Pushing…'
                    : confirmPushAll
                      ? `Confirm: push ALL ${totalPending} rebills · ${usd(data?.total_pending_amount)} to every customer`
                      : `Push (${selectAllMatching ? totalPending : selected.size})`}
              </button>
              {confirmPushAll && (
                <button onClick={() => setConfirmPushAll(false)} className="h-8 px-2 text-[11px] text-ink-400 hover:text-ink-700">Cancel</button>
              )}

              {/* Dispute — selected rows only, two-click confirm with an optional reason */}
              {!selectAllMatching && (
                <>
                  {confirmDispute && (
                    <input value={disputeReason} onChange={e => setDisputeReason(e.target.value)}
                      placeholder="Reason — goes to the carrier (optional)"
                      className="h-8 w-64 px-2 border border-ink-200 rounded-md text-[12px]" />
                  )}
                  <button
                    onClick={() => {
                      if (!confirmDispute) { setConfirmDispute(true); setConfirmVoid(false); return }
                      disputeMut.mutate([...selected])
                    }}
                    disabled={disputeMut.isPending || pushRunning}
                    className={`h-8 px-4 rounded-md text-[12px] font-medium transition-colors disabled:opacity-50 ${
                      confirmDispute ? 'bg-amber-600 text-white hover:bg-amber-700' : 'border border-amber-300 text-amber-700 hover:bg-amber-50'
                    }`}>
                    {disputeMut.isPending ? 'Moving…' : confirmDispute ? `Confirm Dispute (${selected.size})` : `Dispute (${selected.size})`}
                  </button>
                  {confirmDispute && (
                    <button onClick={() => { setConfirmDispute(false); setDisputeReason('') }}
                      className="h-8 px-2 text-[11px] text-ink-400 hover:text-ink-700">Cancel</button>
                  )}
                </>
              )}

              {/* Void — two-click confirm */}
              <button
                onClick={() => {
                  if (!confirmVoid) { setConfirmVoid(true); setConfirmPushAll(false); return }
                  if (selectAllMatching) voidByFilterMut.mutate()
                  else voidMut.mutate([...selected])
                }}
                disabled={!canManage || voidMut.isPending || voidByFilterMut.isPending}
                title={canManage ? undefined : 'Needs the manage rebills permission'}
                className={`h-8 px-4 rounded-md text-[12px] font-medium transition-colors disabled:opacity-50 ${
                  confirmVoid ? 'bg-flame-600 text-white hover:bg-flame-700' : 'border border-flame-300 text-flame-600 hover:bg-flame-50'
                }`}>
                {(voidMut.isPending || voidByFilterMut.isPending)
                  ? 'Voiding…'
                  : confirmVoid
                    ? allAtOnce
                      ? `Confirm: void ALL ${total} rebills · ${usd(data?.total_amount)}${totalWaiting > 0 ? ` incl. ${totalWaiting} waiting` : ''}`
                      : `Confirm Void (${selectAllMatching ? total : selected.size}${selectAllMatching && totalWaiting > 0 ? ` incl. ${totalWaiting} waiting` : ''})`
                    : `Void (${selectAllMatching ? total : selected.size})`}
              </button>

              {confirmVoid && (
                <button onClick={() => setConfirmVoid(false)} className="h-8 px-2 text-[11px] text-ink-400 hover:text-ink-700">Cancel</button>
              )}

              {selectAllMatching && (
                <button onClick={() => { setSelectAllMatching(false); setSelected(new Set()) }} className="h-8 px-2 text-[11px] text-ink-400 hover:text-ink-700">Clear selection</button>
              )}
            </>
          )}
        </div>
      </div>

      {pushRunning && (
        <div className="shrink-0 px-3 py-2 rounded-md border border-mint-200 bg-mint-50 text-[12px] text-mint-800">
          <div className="flex items-center justify-between font-semibold">
            <span>
              Pushed {pushJob?.pushed ?? 0} / {pushJob?.total ?? '…'}
              {pushJob?.failed ? <span className="text-flame-700"> · {pushJob.failed} failed</span> : null}
            </span>
            <span className="font-normal text-mint-700">sending to ByeLabel — you can leave this page</span>
          </div>
          <div className="mt-1.5 h-1.5 rounded-full bg-mint-100 overflow-hidden">
            <div className="h-full bg-mint-600 transition-all"
                 style={{ width: `${pushJob?.total ? Math.round(100 * (pushJob.pushed + pushJob.failed) / pushJob.total) : 0}%` }} />
          </div>
        </div>
      )}

      {banner && (
        <div className={`shrink-0 px-3 py-2 rounded-md text-[12px] font-medium flex items-center justify-between ${banner.type === 'ok' ? 'bg-mint-50 text-mint-800 border border-mint-200' : 'bg-flame-50 text-flame-800 border border-flame-200'}`}>
          <span>{banner.msg}</span>
          <button onClick={() => setBanner(null)} className="text-inherit opacity-60 hover:opacity-100 ml-4"><Icon name="x" className="w-3.5 h-3.5" /></button>
        </div>
      )}

      {/* Table */}
      <div className="flex-1 min-h-0 overflow-y-auto border border-ink-100 rounded-lg">
        {isLoading ? (
          <div className="flex items-center justify-center h-full text-[13px] text-ink-400">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="flex items-center justify-center h-full text-[13px] text-ink-400">No pending rebills.</div>
        ) : (
          <table className="w-full text-[12px] min-w-[960px]">
            <thead className="sticky top-0 bg-white z-10 border-b border-ink-100">
              <tr>
                <th className="py-2 px-3 w-8">
                  <input type="checkbox" checked={selected.size === pushable.length && pushable.length > 0}
                    onChange={toggleAll} className="rounded" />
                </th>
                <SortableHeader col="status"           label="Status"    sort={pendingSort} onSort={onPendingSort} />
                <SortableHeader col="customer_name"    label="Customer"  sort={pendingSort} onSort={onPendingSort} />
                <SortableHeader col="shipment_number"  label="Tracking"  sort={pendingSort} onSort={onPendingSort} />
                <SortableHeader col="carrier"          label="Carrier"   sort={pendingSort} onSort={onPendingSort} />
                <SortableHeader col="adjustment_name"  label="Category"  sort={pendingSort} onSort={onPendingSort} />
                <SortableHeader col="shipment_date"    label="Ship Date" sort={pendingSort} onSort={onPendingSort} />
                <SortableHeader col="rebill_amount"    label="Amount"    sort={pendingSort} onSort={onPendingSort} numeric />
                <SortableHeader col="carrier_invoice_date" label="Age"   sort={pendingSort} onSort={onPendingSort} />
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.rebill_id}
                  onClick={() => setDetailId(r.rebill_id)}
                  style={detailId === r.rebill_id ? { backgroundColor: '#e4e4e7', borderLeft: '3px solid #18181b' } : undefined}
                  className={`border-b border-ink-50 cursor-pointer transition-colors ${selected.has(r.rebill_id) ? 'bg-mint-50' : 'hover:bg-ink-50/40'}`}>
                  <td className="py-1.5 px-3" onClick={e => e.stopPropagation()}>
                    {r.status === 'PENDING' && (
                      <input type="checkbox" checked={selected.has(r.rebill_id)} onChange={() => { /* handled by onClick (needs shiftKey) */ }}
                        onClick={e => toggleRow(r.rebill_id, e.shiftKey)}
                        onMouseDown={e => { if (e.shiftKey) e.preventDefault() /* no text highlight on shift-click */ }}
                        title="Shift-click to select a range" className="rounded" />
                    )}
                  </td>
                  <td className="py-1.5 px-2"><StatusBadge status={r.status} /></td>
                  <td className="py-1.5 px-2 font-medium text-ink-800 max-w-[140px] truncate">{r.customer_name}</td>
                  <td className="py-1.5 px-2"><CopyCell value={r.shipment_number} /></td>
                  <td className="py-1.5 px-2 text-ink-500 uppercase text-[10px]">{r.carrier}</td>
                  <td className="py-1.5 px-2 text-ink-600 max-w-[160px] truncate" title={r.adjustment_name ?? r.adjustment_category ?? ''}>
                    {r.adjustment_name || r.adjustment_category || '—'}
                  </td>
                  <td className="py-1.5 px-2 text-ink-400 whitespace-nowrap">{r.shipment_date?.slice(0, 10) ?? '—'}</td>
                  <td className={`py-1.5 px-2 text-right tabular-nums font-semibold ${Number(r.rebill_amount ?? 0) < 0 ? 'text-mint-700' : 'text-ink-800'}`}>{fmt(r.rebill_amount)}</td>
                  <td className="py-1.5 px-2"><AgeBadge dateStr={r.carrier_invoice_date} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center gap-2 shrink-0 justify-end">
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
            className="h-7 px-3 text-[12px] border border-ink-200 rounded-md hover:bg-ink-50 disabled:opacity-40">‹ Prev</button>
          <span className="text-[12px] text-ink-500">Page {page + 1} / {pages}</span>
          <button onClick={() => setPage(p => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1}
            className="h-7 px-3 text-[12px] border border-ink-200 rounded-md hover:bg-ink-50 disabled:opacity-40">Next ›</button>
        </div>
      )}

      <DetailDrawer rebillId={detailId} onClose={() => setDetailId(null)} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Pushed tab
// ---------------------------------------------------------------------------
function PushedTab() {
  const qc = useQueryClient()
  const { token, can } = useAuth()
  // push / void / reset need manage_rebills (enforced by the API too)
  const canManage = can('manage_rebills')
  const { data: customers = [] } = useQuery<{ customer_name: string }[]>({
    queryKey: ['adj-customers'],
    queryFn: () => fetch(`${API}/customers`, { headers: authHeaders(token) }).then(r => r.json()).then(d => Array.isArray(d) ? d : []),
  })

  const [draft, setDraft] = useState<FilterState>({ customer: '', carrier: '', category: '', shipment: '', since: '', until: '' })
  const [applied, setApplied] = useState<FilterState>({ customer: '', carrier: '', category: '', shipment: '', since: '', until: '' })
  const [page, setPage] = useState(0)
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [selectAllMatching, setSelectAllMatching] = useState(false)
  const [detailId, setDetailId] = useState<number | null>(null)
  const [confirmReset, setConfirmReset] = useState(false)
  const [banner, setBanner] = useState<{ type: 'ok' | 'err'; msg: string } | null>(null)
  const [pushedSort, setPushedSort] = useState<SortState>({ col: 'byelabel_pushed_at', dir: 'desc' })

  function onPushedSort(col: string) {
    setPushedSort(prev => prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' })
    setPage(0)
  }

  const params = new URLSearchParams()
  if (applied.customer)  params.set('customer',  applied.customer)
  if (applied.carrier)   params.set('carrier',   applied.carrier)
  if (applied.category)  params.set('category',  applied.category)
  if (applied.shipment)  params.set('shipment',  applied.shipment)
  if (applied.since)     params.set('since',     applied.since)
  if (applied.until)     params.set('until',     applied.until)
  params.set('limit', String(PAGE))
  params.set('offset', String(page * PAGE))
  params.set('sort_col', pushedSort.col)
  params.set('sort_dir', pushedSort.dir)

  const { data, isLoading, isFetching } = useQuery<{ rows: PushedRebill[]; total: number }>({
    queryKey: ['adj-pushed', applied, page, pushedSort],
    queryFn: () => fetch(`${API}/pushed?${params}`, { headers: authHeaders(token) }).then(r => r.json())
      .then(d => Array.isArray(d?.rows) ? d : { rows: [], total: 0 }),
  })

  const rows  = data?.rows  ?? []
  const total = data?.total ?? 0
  const pages = Math.ceil(total / PAGE)

  const EMPTY_FILTER: FilterState = { customer: '', carrier: '', category: '', shipment: '', since: '', until: '' }

  function applyFilters() { setApplied({ ...draft }); setPage(0); setSelected(new Set()); setSelectAllMatching(false); setConfirmReset(false) }

  function clearFilters() {
    setDraft(EMPTY_FILTER)
    setApplied(EMPTY_FILTER)
    setPage(0)
    setSelected(new Set())
    setSelectAllMatching(false)
    setConfirmReset(false)
  }
  function toggleAll() {
    if (selected.size === rows.length && rows.length > 0) { setSelected(new Set()); setSelectAllMatching(false) }
    else setSelected(new Set(rows.map(r => r.rebill_id)))
  }
  // shift-click selects / clears every row between the last click and this one
  const lastClicked = useRef<number | null>(null)
  function toggleRow(id: number, shift = false) {
    setSelectAllMatching(false)
    const ids = shift ? rangeIds(rows.map(r => r.rebill_id), lastClicked.current, id) : [id]
    setSelected(prev => {
      const s = new Set(prev); const on = !s.has(id)
      ids.forEach(x => on ? s.add(x) : s.delete(x))
      return s
    })
    lastClicked.current = id
  }

  const resetMut = useMutation({
    mutationFn: (ids: number[]) =>
      fetch(`${API}/reset`, { method: 'POST', headers: { ...authHeaders(token), 'Content-Type': 'application/json' }, body: JSON.stringify({ rebill_ids: ids }) }).then(r => r.json()),
    onSuccess: (_, ids) => {
      setBanner({ type: 'ok', msg: `Reset ${ids.length} rebill(s) to pending.` })
      setSelected(new Set()); setSelectAllMatching(false); setConfirmReset(false)
      qc.invalidateQueries({ queryKey: ['adj-pushed'] })
      qc.invalidateQueries({ queryKey: ['adj-pending'] })
      qc.invalidateQueries({ queryKey: ['adj-summary'] })
      qc.invalidateQueries({ queryKey: ['nav-counts'] })
    },
    onError: (e: Error) => setBanner({ type: 'err', msg: e.message }),
  })

  const resetByFilterMut = useMutation({
    mutationFn: () => {
      const body: Record<string, string> = {}
      if (applied.customer) body.customer = applied.customer
      if (applied.carrier)  body.carrier  = applied.carrier
      if (applied.category) body.category = applied.category
      if (applied.since)    body.since    = applied.since
      if (applied.until)    body.until    = applied.until
      return fetch(`${API}/reset-by-filter`, { method: 'POST', headers: { ...authHeaders(token), 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).then(r => r.json())
    },
    onSuccess: (d) => {
      setBanner({ type: 'ok', msg: `Reset ${d.reset ?? total} rebill(s) to pending.` })
      setSelected(new Set()); setSelectAllMatching(false); setConfirmReset(false)
      qc.invalidateQueries({ queryKey: ['adj-pushed'] })
      qc.invalidateQueries({ queryKey: ['adj-pending'] })
      qc.invalidateQueries({ queryKey: ['adj-summary'] })
      qc.invalidateQueries({ queryKey: ['nav-counts'] })
    },
    onError: (e: Error) => setBanner({ type: 'err', msg: e.message }),
  })

  return (
    <div className="flex flex-col h-full gap-3">
      <FilterBar f={draft} customers={customers} onChange={p => setDraft(prev => ({ ...prev, ...p }))} onApply={applyFilters} onClear={clearFilters} />

      {/* Action bar */}
      <div className="flex items-center gap-2 shrink-0 flex-wrap">
        <span className="text-[12px] text-ink-500">{total} pushed{isFetching ? ' · refreshing…' : ''}</span>

        {/* "Select all filtered" prompt */}
        {!selectAllMatching && selected.size === rows.length && rows.length > 0 && total > rows.length && (
          <button onClick={() => setSelectAllMatching(true)}
            className="text-[12px] text-mint-700 underline underline-offset-2 hover:text-mint-900 ml-1">
            Select all {total} filtered →
          </button>
        )}
        {selectAllMatching && (
          <span className="text-[12px] font-semibold text-mint-700 ml-1">All {total} filtered selected</span>
        )}
        {(selected.size > 0 || selectAllMatching) && (
          <span className="text-[12px] text-ink-400 ml-1">{selectAllMatching ? total : selected.size} selected</span>
        )}

        <div className="ml-auto flex items-center gap-2">
          {(selected.size > 0 || selectAllMatching) && (
            <>
              <button
                onClick={() => {
                  if (!confirmReset) { setConfirmReset(true); return }
                  if (selectAllMatching) resetByFilterMut.mutate()
                  else resetMut.mutate([...selected])
                }}
                disabled={!canManage || resetMut.isPending || resetByFilterMut.isPending}
                title={canManage ? undefined : 'Needs the manage rebills permission'}
                className={`h-8 px-4 rounded-md text-[12px] font-medium transition-colors disabled:opacity-50 ${confirmReset ? 'bg-flame-600 text-white hover:bg-flame-700' : 'border border-flame-300 text-flame-600 hover:bg-flame-50'}`}>
                {(resetMut.isPending || resetByFilterMut.isPending)
                  ? 'Resetting…'
                  : confirmReset
                    ? `Confirm Reset (${selectAllMatching ? total : selected.size})`
                    : `Reset to Pending (${selectAllMatching ? total : selected.size})`}
              </button>
              {confirmReset && !(resetMut.isPending || resetByFilterMut.isPending) && (
                <button onClick={() => setConfirmReset(false)} className="h-8 px-2 text-[11px] text-ink-400 hover:text-ink-700">Cancel</button>
              )}
              {selectAllMatching && (
                <button onClick={() => { setSelectAllMatching(false); setSelected(new Set()) }} className="h-8 px-2 text-[11px] text-ink-400 hover:text-ink-700">Clear selection</button>
              )}
            </>
          )}
        </div>
      </div>

      {banner && (
        <div className={`shrink-0 px-3 py-2 rounded-md text-[12px] font-medium flex items-center justify-between ${banner.type === 'ok' ? 'bg-mint-50 text-mint-800 border border-mint-200' : 'bg-flame-50 text-flame-800 border border-flame-200'}`}>
          <span>{banner.msg}</span>
          <button onClick={() => setBanner(null)} className="opacity-60 hover:opacity-100 ml-4"><Icon name="x" className="w-3.5 h-3.5" /></button>
        </div>
      )}

      {/* Table */}
      <div className="flex-1 min-h-0 overflow-y-auto border border-ink-100 rounded-lg">
        {isLoading ? (
          <div className="flex items-center justify-center h-full text-[13px] text-ink-400">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="flex items-center justify-center h-full text-[13px] text-ink-400">No pushed rebills in this range.</div>
        ) : (
          <table className="w-full text-[12px] min-w-[960px]">
            <thead className="sticky top-0 bg-white z-10 border-b border-ink-100">
              <tr>
                <th className="py-2 px-3 w-8">
                  <input type="checkbox" checked={selected.size === rows.length && rows.length > 0}
                    onChange={toggleAll} className="rounded" />
                </th>
                <SortableHeader col="customer_name"      label="Customer"  sort={pushedSort} onSort={onPushedSort} />
                <SortableHeader col="shipment_number"    label="Tracking"  sort={pushedSort} onSort={onPushedSort} />
                <SortableHeader col="carrier"            label="Carrier"   sort={pushedSort} onSort={onPushedSort} />
                <SortableHeader col="adjustment_name"    label="Category"  sort={pushedSort} onSort={onPushedSort} />
                <SortableHeader col="shipment_date"      label="Ship Date" sort={pushedSort} onSort={onPushedSort} />
                <SortableHeader col="rebill_amount"      label="Amount"    sort={pushedSort} onSort={onPushedSort} numeric />
                <SortableHeader col="invoice_number"     label="Invoice"   sort={pushedSort} onSort={onPushedSort} />
                <SortableHeader col="byelabel_pushed_at" label="Pushed At" sort={pushedSort} onSort={onPushedSort} />
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.rebill_id}
                  onClick={() => setDetailId(r.rebill_id)}
                  style={detailId === r.rebill_id ? { backgroundColor: '#e4e4e7', borderLeft: '3px solid #18181b' } : undefined}
                  className={`border-b border-ink-50 cursor-pointer transition-colors ${selected.has(r.rebill_id) ? 'bg-honey-50' : 'hover:bg-ink-50/40'}`}>
                  <td className="py-1.5 px-3" onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.has(r.rebill_id)} onChange={() => { /* handled by onClick (needs shiftKey) */ }}
                        onClick={e => toggleRow(r.rebill_id, e.shiftKey)}
                        onMouseDown={e => { if (e.shiftKey) e.preventDefault() /* no text highlight on shift-click */ }}
                        title="Shift-click to select a range" className="rounded" />
                  </td>
                  <td className="py-1.5 px-2 font-medium text-ink-800 max-w-[140px] truncate">{r.customer_name}</td>
                  <td className="py-1.5 px-2"><CopyCell value={r.shipment_number} /></td>
                  <td className="py-1.5 px-2 text-ink-500 uppercase text-[10px]">{r.carrier}</td>
                  <td className="py-1.5 px-2 text-ink-600 max-w-[160px] truncate" title={r.adjustment_name ?? r.adjustment_category ?? ''}>
                    {r.adjustment_name || r.adjustment_category || '—'}
                  </td>
                  <td className="py-1.5 px-2 text-ink-400 whitespace-nowrap">{r.shipment_date?.slice(0, 10) ?? '—'}</td>
                  <td className={`py-1.5 px-2 text-right tabular-nums font-semibold ${Number(r.rebill_amount ?? 0) < 0 ? 'text-mint-700' : 'text-ink-800'}`}>{fmt(r.rebill_amount)}</td>
                  <td className="py-1.5 px-2 text-ink-400">{r.invoice_number ?? '—'}</td>
                  <td className="py-1.5 px-2 text-ink-400 whitespace-nowrap">{r.byelabel_pushed_at?.slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {pages > 1 && (
        <div className="flex items-center gap-2 shrink-0 justify-end">
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
            className="h-7 px-3 text-[12px] border border-ink-200 rounded-md hover:bg-ink-50 disabled:opacity-40">‹ Prev</button>
          <span className="text-[12px] text-ink-500">Page {page + 1} / {pages}</span>
          <button onClick={() => setPage(p => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1}
            className="h-7 px-3 text-[12px] border border-ink-200 rounded-md hover:bg-ink-50 disabled:opacity-40">Next ›</button>
        </div>
      )}

      <DetailDrawer rebillId={detailId} onClose={() => setDetailId(null)} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Settings tab — auto-push toggle + exclusions
// ---------------------------------------------------------------------------
function SettingsTab() {
  const qc = useQueryClient()
  const { token, can, isAdmin } = useAuth()
  // auto-push switch: owner/admin; exclusions: manage_rebills (enforced by the API too)
  const canManage = can('manage_rebills')
  const { data: customers = [] } = useQuery<{ customer_name: string }[]>({
    queryKey: ['adj-customers'],
    queryFn: () => fetch(`${API}/customers`, { headers: authHeaders(token) }).then(r => r.json()).then(d => Array.isArray(d) ? d : []),
  })
  const { data: autoPush, isLoading: apLoading } = useQuery<{ enabled: boolean }>({
    queryKey: ['byelabel-auto-push'],
    queryFn: () => fetch(`${API}/auto-push`, { headers: authHeaders(token) }).then(r => r.json()),
  })
  const { data: exclusions = [], isLoading: excLoading } = useQuery<Exclusion[]>({
    queryKey: ['byelabel-sync-exclusions'],
    queryFn: () => fetch(`${API}/exclusions`, { headers: authHeaders(token) }).then(r => r.json()).then(d => Array.isArray(d) ? d : []),
  })

  const apMut = useMutation({
    mutationFn: (enabled: boolean) =>
      fetch(`${API}/auto-push`, { method: 'PATCH', headers: { ...authHeaders(token), 'Content-Type': 'application/json' }, body: JSON.stringify({ enabled }) }).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['byelabel-auto-push'] }),
  })

  const [selCust, setSelCust] = useState('')
  const [selScope, setSelScope] = useState<Scope>('both')
  const [addErr, setAddErr] = useState('')

  const addMut = useMutation({
    mutationFn: (body: { customer_name: string; scope: string }) =>
      fetch(`${API}/exclusions`, { method: 'POST', headers: { ...authHeaders(token), 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).then(r => r.json()),
    onSuccess: d => {
      if (d.ok) { qc.invalidateQueries({ queryKey: ['byelabel-sync-exclusions'] }); setSelCust(''); setAddErr('') }
      else setAddErr(d.error ?? 'Error')
    },
  })
  const delMut = useMutation({
    mutationFn: (name: string) =>
      fetch(`${API}/exclusions/${encodeURIComponent(name)}`, { method: 'DELETE', headers: authHeaders(token) }).then(r => r.json()),
    onSuccess: d => {
      // a credit (invoiced) customer's exclusion is refused by the API
      if (d?.ok) { qc.invalidateQueries({ queryKey: ['byelabel-sync-exclusions'] }); setAddErr('') }
      else setAddErr(d?.error ?? 'Error')
    },
  })

  const enabled = autoPush?.enabled ?? false

  return (
    <div className="flex flex-col h-full gap-6 overflow-y-auto pr-1">
      {/* Auto-push toggle */}
      <div className="rounded-lg border border-ink-100 p-5 flex items-center justify-between gap-4">
        <div>
          <div className="text-[14px] font-semibold text-ink-800">Auto-Push to ByeLabel</div>
          <p className="text-[12px] text-ink-500 mt-0.5">
            {enabled ? 'New rebills are pushed automatically after each carrier invoice run.' : 'New rebills are held — push them manually from the Pending tab.'}
          </p>
          {enabled && <p className="text-[11px] text-ink-400 mt-1">Customers in the exclusion list below are skipped even when ON.</p>}
        </div>
        <button onClick={() => apMut.mutate(!enabled)} disabled={!isAdmin || apLoading || apMut.isPending}
          title={isAdmin ? undefined : 'Only an owner or admin can change this'}
          className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 disabled:opacity-50 ${enabled ? 'bg-mint-600' : 'bg-ink-200'}`}
          role="switch" aria-checked={enabled}>
          <span className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition duration-200 ${enabled ? 'translate-x-5' : 'translate-x-0'}`} />
        </button>
      </div>

      {/* Exclusions */}
      <div className="rounded-lg border border-ink-100 p-5 flex flex-col gap-4">
        <div className="text-[14px] font-semibold text-ink-800">Push Exclusions</div>

        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex flex-col gap-0.5">
            <label className="text-[10px] font-semibold text-ink-400 uppercase tracking-wide">Customer</label>
            <select value={selCust} onChange={e => setSelCust(e.target.value)}
              className="h-8 pl-2 pr-6 border border-ink-200 rounded-md text-[12px] bg-white focus-ring">
              <option value="">— select —</option>
              {customers.map(c => <option key={c.customer_name} value={c.customer_name}>{c.customer_name}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-0.5">
            <label className="text-[10px] font-semibold text-ink-400 uppercase tracking-wide">Block</label>
            <div className="flex gap-1">
              {SCOPES.map(s => (
                <button key={s.value} onClick={() => setSelScope(s.value)} title={s.desc}
                  className={`h-8 px-3 rounded-md text-[11px] font-medium border transition-colors ${selScope === s.value ? 'bg-ink-800 text-white border-ink-800' : 'bg-white text-ink-600 border-ink-200 hover:bg-ink-50'}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <button onClick={() => { if (!selCust) { setAddErr('Select a customer'); return } setAddErr(''); addMut.mutate({ customer_name: selCust, scope: selScope }) }}
            disabled={!canManage || addMut.isPending}
            title={canManage ? undefined : 'Needs the manage rebills permission'}
            className="h-8 px-4 bg-ink-800 text-white rounded-md text-[12px] font-medium hover:bg-ink-700 disabled:opacity-50 transition-colors">
            {addMut.isPending ? 'Saving…' : 'Add / Update'}
          </button>
          {addErr && <span className="text-[11px] text-flame-600 self-center">{addErr}</span>}
        </div>

        {excLoading ? <p className="text-[12px] text-ink-400">Loading…</p>
          : exclusions.length === 0 ? <p className="text-[12px] text-ink-400">No exclusions — all customers are included.</p>
          : (
            <div className="overflow-y-auto max-h-48 border border-ink-100 rounded-md">
              <table className="w-full text-[12px]">
                <thead className="sticky top-0 bg-white border-b border-ink-100">
                  <tr>
                    {['Customer','Block','Added',''].map(h => (
                      <th key={h} className="text-left py-2 px-3 text-[10px] font-semibold text-ink-400 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {exclusions.map(exc => (
                    <tr key={exc.customer_name} className="border-b border-ink-50 hover:bg-ink-50/40">
                      <td className="py-1.5 px-3 font-medium text-ink-800">{exc.customer_name}</td>
                      <td className="py-1.5 px-3">{scopeBadge(exc.scope)}</td>
                      <td className="py-1.5 px-3 text-ink-400">{exc.created_at?.slice(0, 10)}</td>
                      <td className="py-1.5 px-3 text-right">
                        <button onClick={() => delMut.mutate(exc.customer_name)} disabled={!canManage || delMut.isPending}
                          title={canManage ? undefined : 'Needs the manage rebills permission'}
                          className="text-flame-500 hover:text-flame-700 text-[11px] font-medium disabled:opacity-40">Remove</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main view
// ---------------------------------------------------------------------------
type Tab = 'pending' | 'pushed' | 'settings'

export function CarrierAdjustmentsView() {
  const [tab, setTab] = useState<Tab>('pending')

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col px-8 py-5 gap-4 overflow-hidden">
      {/* Header */}
      <div className="flex items-start justify-between shrink-0">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight text-ink-900">Carrier Adjustments</h1>
          <p className="text-[12px] text-ink-400 mt-0.5">Carrier rebills auto-detected nightly · push to ByeLabel when ready</p>
        </div>
      </div>

      {/* KPI chips */}
      <KpiChips />

      {/* Tabs */}
      <div className="flex gap-1 border-b border-ink-100 shrink-0">
        {([
          { id: 'pending',  label: 'Pending' },
          { id: 'pushed',   label: 'Pushed' },
          { id: 'settings', label: 'Settings' },
        ] as { id: Tab; label: string }[]).map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-[13px] font-medium border-b-2 -mb-px transition-colors ${
              tab === t.id ? 'border-ink-800 text-ink-900' : 'border-transparent text-ink-400 hover:text-ink-700'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content — fills remaining height */}
      <div className="flex-1 min-h-0">
        {tab === 'pending'  && <PendingTab />}
        {tab === 'pushed'   && <PushedTab />}
        {tab === 'settings' && <SettingsTab />}
      </div>
    </div>
  )
}
